import { useState } from "react";
import { motion } from "framer-motion";
import { WARRANTIES } from "@/content";
import { ShieldCheck, Check } from "lucide-react";

export default function Warranties() {
  const [active, setActive] = useState(0);
  const group = WARRANTIES.groups[active];

  return (
    <section
      id="warranties"
      data-testid="section-warranties"
      className="relative py-24 lg:py-36 bg-[#EFEADB] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mx-auto mb-12 lg:mb-16 text-center"
        >
          <span
            className="block text-xs text-brand-accent mb-5"
            data-testid="warranties-kicker"
          >
            {WARRANTIES.kicker}
          </span>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl text-brand-ink mb-6 leading-tight"
            data-testid="warranties-headline"
          >
            {WARRANTIES.headline}
          </h2>
          <p className="text-base lg:text-lg text-brand-mute leading-loose">
            {WARRANTIES.description}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Tabs */}
          <div className="lg:col-span-4 flex flex-row lg:flex-col gap-2 overflow-x-auto hide-scrollbar pb-2 lg:pb-0">
            {WARRANTIES.groups.map((g, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`flex items-center gap-3 px-5 py-4 text-right border whitespace-nowrap lg:whitespace-normal transition-all duration-300 ${
                  active === i
                    ? "bg-brand-olive border-brand-olive text-brand-cream shadow-md rounded-xl"
                    : "bg-brand-cream border-brand-border text-brand-ink hover:border-brand-olive rounded-xl"
                }`}
                data-testid={`warranties-tab-${i}`}
              >
                <ShieldCheck size={18} strokeWidth={1.4} />
                <span className="text-sm font-medium">
                  {g.title}
                </span>
              </button>
            ))}
          </div>

          {/* Content */}
          <motion.div
            key={active}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-8 bg-brand-cream border border-brand-border p-8 lg:p-12 rounded-2xl"
            data-testid="warranties-content"
          >
            <h3 className="text-2xl lg:text-3xl text-brand-ink mb-8 font-serif">
              ضمان {group.title}
            </h3>
            <ul className="grid sm:grid-cols-2 gap-4">
              {group.items.map((item, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-3 border-b border-brand-border/60 pb-4"
                  data-testid={`warranty-item-${idx}`}
                >
                  <span className="mt-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-olive/10 text-brand-olive flex-shrink-0">
                    <Check size={14} strokeWidth={2.5} />
                  </span>
                  <div className="flex-1">
                    <div className="text-sm lg:text-base text-brand-ink leading-snug">{item.name}</div>
                    <div className="text-xs text-brand-accent mt-1 font-medium">
                      {item.period}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* After-sale services strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-16 bg-brand-olive text-brand-cream p-8 lg:p-12 rounded-2xl"
          data-testid="after-sales-strip"
        >
          <h3 className="text-2xl lg:text-3xl mb-2 font-serif">في مُيسر، عناية لا تنتهي</h3>
          <p className="text-brand-cream/75 mb-8 max-w-3xl">
            خدمات ما بعد البيع لضمان تجربة سكنية متكاملة، تبدأ بعد إتمام البيع وتستمر معك.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
            {WARRANTIES.afterSales.map((s, i) => (
              <div key={i} className="flex items-start gap-3 text-sm text-brand-cream/90" data-testid={`after-sale-${i}`}>
                <span className="text-brand-accent mt-1">◆</span>
                <span>{s}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

